import { privateApi } from '@/config/axios.config';

export interface BusinessMetrics {
  totalProperties: number;
  activeProperties: number;
  totalLeads: number;
  conversionRate: number;
  monthlyRevenue: number;
  revenueGrowth: number;
  averagePrice: number;
  priceGrowth: number;
}

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  stage: 'new' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';
  source: 'website' | 'referral' | 'social' | 'advertising' | 'other';
  budget: number;
  assignedTo?: string;
  createdAt: Date;
  lastContact: Date;
  notes: string;
}

export interface Property {
  id: string;
  title: string;
  type: 'house' | 'apartment' | 'land' | 'commercial';
  status: 'active' | 'sold' | 'rented' | 'inactive';
  price: number;
  location: string;
  bedrooms?: number;
  bathrooms?: number;
  area: number;
  createdAt: Date;
  lastUpdated: Date;
}

export interface AgencyDashboardData {
  metrics: BusinessMetrics;
  recentLeads: Lead[];
  recentProperties: Property[];
  totalLeads: number;
  totalProperties: number;
  activeProperties: number;
  soldProperties: number;
  rentedProperties: number;
  leadsStats?: {
    new: number;
    active: number;
    closed: number;
  };
  topViewedProperties?: Array<{
    id: string;
    title: string;
    views: number;
    price: number;
  }>;
  projectedRevenue?: number;
  actualRevenue?: number;
}

export const agencyDashboardApiService = {
  // Obtener datos completos del dashboard de la agencia
  async getDashboardData(): Promise<AgencyDashboardData> {
    const response = await privateApi.get('/agency/dashboard');
    return response.data.data || response.data;
  },

  // Obtener métricas del dashboard
  async getMetrics(): Promise<BusinessMetrics> {
    const response = await privateApi.get('/agency/dashboard/metrics');
    return response.data.data;
  },

  // Obtener leads recientes
  async getRecentLeads(limit: number = 10): Promise<Lead[]> {
    const response = await privateApi.get(
      `/agency/dashboard/leads?limit=${limit}`
    );
    return response.data.data;
  },

  // Obtener propiedades recientes
  async getRecentProperties(limit: number = 10): Promise<Property[]> {
    const response = await privateApi.get(
      `/agency/dashboard/properties?limit=${limit}`
    );
    return response.data.data;
  },

  // Obtener estadísticas por período
  async getStatsByPeriod(period: '7d' | '30d' | '90d' | '1y'): Promise<{
    properties: number;
    leads: number;
    revenue: number;
    conversions: number;
    growth: number;
  }> {
    const response = await privateApi.get(`/agency/dashboard/stats/${period}`);
    return response.data.data;
  },

  // Obtener métricas de rendimiento de la agencia
  async getPerformanceMetrics(): Promise<{
    averageDealSize: number;
    conversionRate: number;
    agentProductivity: number;
    clientSatisfaction: number;
    marketShare: number;
  }> {
    const response = await privateApi.get('/agency/dashboard/performance');
    return response.data.data;
  },

  // Obtener alertas de la agencia
  async getAgencyAlerts(): Promise<{
    pendingLeads: number;
    expiringListings: number;
    overdueCommissions: number;
    inactiveAgents: number;
    lowInventory: boolean;
  }> {
    const response = await privateApi.get('/agency/dashboard/alerts');
    return response.data.data;
  },

  // Obtener resumen financiero
  async getFinancialSummary(): Promise<{
    monthlyRevenue: number;
    monthlyExpenses: number;
    netIncome: number;
    outstandingCommissions: number;
    totalAssets: number;
    cashFlow: Array<{
      month: string;
      income: number;
      expenses: number;
      net: number;
    }>;
  }> {
    const response = await privateApi.get(
      '/agency/dashboard/financial-summary'
    );
    return response.data.data;
  },

  // Obtener análisis de mercado
  async getMarketAnalysis(): Promise<{
    marketTrends: Array<{
      period: string;
      averagePrice: number;
      salesVolume: number;
      daysOnMarket: number;
      inventory: number;
    }>;
    competitorAnalysis: {
      averageCommission: number;
      marketShare: number;
      topCompetitors: Array<{
        name: string;
        marketShare: number;
        properties: number;
      }>;
    };
    priceAnalysis: {
      pricePerSqft: number;
      priceGrowth: number;
      affordabilityIndex: number;
    };
  }> {
    const response = await privateApi.get('/agency/dashboard/market-analysis');
    return response.data.data;
  },

  // Obtener métricas de agentes
  async getAgentsMetrics(): Promise<{
    totalAgents: number;
    activeAgents: number;
    topPerformers: Array<{
      id: string;
      name: string;
      sales: number;
      commission: number;
      leads: number;
    }>;
    averagePerformance: {
      salesPerAgent: number;
      commissionPerAgent: number;
      leadsPerAgent: number;
    };
  }> {
    const response = await privateApi.get('/agency/dashboard/agents-metrics');
    return response.data.data;
  },

  // Obtener tendencias de leads
  async getLeadsTrends(): Promise<{
    leadsBySource: Record<string, number>;
    leadsByStage: Record<string, number>;
    conversionFunnel: Array<{
      stage: string;
      count: number;
      percentage: number;
    }>;
    monthlyTrends: Array<{
      month: string;
      newLeads: number;
      convertedLeads: number;
      conversionRate: number;
    }>;
  }> {
    const response = await privateApi.get('/agency/dashboard/leads-trends');
    return response.data.data;
  },

  // Obtener métricas de propiedades
  async getPropertiesMetrics(): Promise<{
    propertiesByType: Record<string, number>;
    propertiesByStatus: Record<string, number>;
    averageDaysOnMarket: number;
    priceDistribution: Array<{
      range: string;
      count: number;
      percentage: number;
    }>;
    locationAnalysis: Array<{
      location: string;
      count: number;
      averagePrice: number;
    }>;
  }> {
    const response = await privateApi.get(
      '/agency/dashboard/properties-metrics'
    );
    return response.data.data;
  },

  // Obtener estadísticas de campañas de marketing
  async getCampaignsStats(): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    activeCampaigns?: number;
  }> {
    const response = await privateApi.get('/marketing/whatsapp-campaigns/stats');
    const data = response.data.data || response.data;
    // Calcular campañas activas (running + scheduled)
    const activeCampaigns = (data.byStatus?.RUNNING || 0) + (data.byStatus?.SCHEDULED || 0);
    return {
      ...data,
      activeCampaigns,
    };
  },
};

export default agencyDashboardApiService;
